import type { LibraryMode } from "@/lib/tier";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { fetchLibraryItems } from "@/lib/library";
import {
  fetchLibraryCategories,
  fetchLibraryCategoryUsageCounts,
  fetchLibraryItemCategoryAssignments,
  hydrateLibraryItemsWithCategories,
  type LibraryCategory,
} from "@/lib/libraryCategories";
import type { Workspace } from "@/hooks/useWorkspace";
import type { LibraryRecord, LinkedExcerpt } from "./myLibraryHelpers";

type Args = {
  active: Workspace | null;
  workspaces: Workspace[];
  libraryMode: LibraryMode;
  selectedItem: LibraryRecord | null;
};

/** Owns My Library data loading: scope resolution, items+categories+assignments
 *  hydration, excerpt-count rollups, and the linked-excerpts side panel. */
export const useMyLibraryDataLoader = ({ active, workspaces, libraryMode, selectedItem }: Args) => {
  const [items, setItems] = useState<LibraryRecord[]>([]);
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [categoryUsageCounts, setCategoryUsageCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [linkedExcerpts, setLinkedExcerpts] = useState<LinkedExcerpt[]>([]);

  const scopeWorkspaceIds = useMemo(() => {
    if (!active) return [] as string[];
    if (libraryMode === "shared" && workspaces.length > 1) return workspaces.map((w) => w.id);
    return [active.id];
  }, [active, libraryMode, workspaces]);

  const refreshLibrary = useCallback(async () => {
    if (!active || scopeWorkspaceIds.length === 0) {
      setItems([]);
      setCategories([]);
      setCategoryUsageCounts({});
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [libraryItems, libraryCategories, usageCounts] = await Promise.all([
        fetchLibraryItems(scopeWorkspaceIds),
        fetchLibraryCategories(active.id),
        fetchLibraryCategoryUsageCounts(active.id),
      ]);

      const [assignments, excerptRows] = await Promise.all([
        fetchLibraryItemCategoryAssignments(libraryItems.map((item) => item.id)),
        libraryItems.length
          ? supabase
              .from("excerpts")
              .select("library_item_id")
              .in("workspace_id", scopeWorkspaceIds)
              .in("library_item_id", libraryItems.map((item) => item.id))
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (excerptRows.error) throw excerptRows.error;

      const excerptCounts = new Map<string, number>();
      (excerptRows.data ?? []).forEach((row) => {
        if (!row.library_item_id) return;
        excerptCounts.set(row.library_item_id, (excerptCounts.get(row.library_item_id) ?? 0) + 1);
      });

      const workspaceIdsByItem = new Map(libraryItems.map((it) => [it.id, it.workspaceIds] as const));

      setCategories(libraryCategories);
      setCategoryUsageCounts(usageCounts);
      setItems(
        hydrateLibraryItemsWithCategories(libraryItems, libraryCategories, assignments).map((item) => ({
          ...item,
          excerptCount: excerptCounts.get(item.id) ?? 0,
          workspaceIds: workspaceIdsByItem.get(item.id) ?? [],
        })),
      );
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [active, scopeWorkspaceIds]);

  useEffect(() => {
    void refreshLibrary();
  }, [refreshLibrary]);

  useEffect(() => {
    if (!selectedItem) {
      setLinkedExcerpts([]);
      return;
    }
    let ignore = false;
    void supabase
      .from("excerpts")
      // Linked-excerpts panel only shows a snippet, so the plaintext preview is sufficient.
      .select("id, body_text, created_at, notes:notes_preview")
      .eq("library_item_id", selectedItem.id)
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (ignore) return;
        if (error) {
          toast.error(error.message);
          return;
        }
        setLinkedExcerpts(((data ?? []) as unknown as LinkedExcerpt[]));
      });
    return () => {
      ignore = true;
    };
  }, [selectedItem]);

  return {
    items,
    setItems,
    categories,
    setCategories,
    categoryUsageCounts,
    setCategoryUsageCounts,
    loading,
    linkedExcerpts,
    scopeWorkspaceIds,
    refreshLibrary,
  };
};
